import React, { useState, useEffect, useContext } from "react";
import PopoverInput from "./inner/popoverInput.jsx";
import PickerFooter from "./inner/pickerFooter.jsx";
import { DataSourceContext, IsMobileContext } from "../context/contexts.jsx";
import { isHotelCode } from "../helpers/geography.js";
import { isVisuallySelected as isVisuallySelectedFor, toggleDestination } from "../helpers/destinationSelection.js";
import { onActivateKeyDown, FOCUS_RING } from "../helpers/a11y.js";

const icon = <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 20.0005C18 21.1051 15.3137 22.0005 12 22.0005C8.68629 22.0005 6 21.1051 6 20.0005M15 9.50049C15 11.1574 13.6569 12.5005 12 12.5005C10.3431 12.5005 9 11.1574 9 9.50049C9 7.84364 10.3431 6.50049 12 6.50049C13.6569 6.50049 15 7.84364 15 9.50049ZM12 2.00049C16.0588 2.00049 19.5 5.42852 19.5 9.58739C19.5 13.8125 16.0028 16.7775 12.7725 18.7937C12.5371 18.9292 12.2709 19.0005 12 19.0005C11.7291 19.0005 11.4629 18.9292 11.2275 18.7937C8.00325 16.7578 4.5 13.8271 4.5 9.58739C4.5 5.42852 7.9412 2.00049 12 2.00049Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path>
</svg>;
// Small map pin shown next to countries, regions and resorts.
const pinIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 9.5C15 11.16 13.66 12.5 12 12.5C10.34 12.5 9 11.16 9 9.5C9 7.84 10.34 6.5 12 6.5C13.66 6.5 15 7.84 15 9.5ZM12 2C16.06 2 19.5 5.43 19.5 9.59C19.5 13.81 16 16.78 12.77 18.79C12.54 18.93 12.27 19 12 19C11.73 19 11.46 18.93 11.23 18.79C8 16.76 4.5 13.83 4.5 9.59C4.5 5.43 7.94 2 12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);
// Tick rendered inside the checkbox; transparent (box reads as empty) until
// the row is selected.
const tickIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
// Checkbox shown on every row - an empty box when unselected, a filled tick
// when selected (matching the airport picker).
const Checkbox = ({ checked }) => (
    <span className={"shrink-0 w-5 h-5 rounded-[5px] border-2 flex items-center justify-center transition-colors " +
        (checked ? "bg-emp border-emp text-white" : "border-content_4 text-transparent")}>
        {tickIcon}
    </span>
);

const DetailedSelectItem = ({ destination, onSelect, selected }) => {
    const [imgError, setImgError] = useState(false);
    const showImage = destination.type == "Hotel" && destination.image && !imgError;
    return (
        <div role="checkbox" tabIndex={0} aria-checked={selected} aria-label={destination.name}
            onKeyDown={onActivateKeyDown(() => onSelect(destination))}
            className={"flex items-center gap-3 rounded-[8px] mr-2 min-h-20 p-2 cursor-pointer " + FOCUS_RING + " " + (selected ? "bg-ocean-100 text-primary" : "text-content_2 hover:bg-content_5")} onClick={() => onSelect(destination)}>
            <Checkbox checked={selected} />
            {/* Fixed-size image box so every row lines up regardless of name
                length; falls back to a pin when the image is missing/broken. */}
            <div className="w-16 h-16 shrink-0 rounded-[8px] overflow-hidden bg-content_5 flex items-center justify-center text-content_3">
                {showImage
                    ? <img className="w-full h-full object-cover" src={destination.image + "?width=100"} onError={() => setImgError(true)} alt="" />
                    : pinIcon}
            </div>
            <div className="flex flex-col min-w-0 justify-center flex-1">
                <div className="text-tiny text-content_3 truncate">{destination.ancestors?.join(" > ")}</div>
                <div className="text-base leading-tight line-clamp-2">{destination.name}</div>
            </div>
        </div>);
}

const SimpleSelectItem = ({ destination, onSelect, type, selected }) => {
    const indent = type == "resort" ? "pl-10" : type == "region" ? "pl-5" : "";
    return (
        <div role="checkbox" tabIndex={0} aria-checked={selected} aria-label={destination.name}
            onKeyDown={onActivateKeyDown(() => onSelect(destination))}
            className={"flex items-center gap-2 rounded-[8px] mr-2 form-checkbox-text-left cursor-pointer " + FOCUS_RING + " " + indent + (selected ? " bg-ocean-100 text-primary" : " text-content_2 hover:bg-content_5")} onClick={() => onSelect(destination)}>
            <Checkbox checked={selected} />
            <span className="shrink-0 text-content_3">{pinIcon}</span>
            <span className={"flex-1 " + (type == "country" ? "font-semibold text-primary" : "")}>{destination.name}</span>
        </div>)
}

const DestinationPicker = ({ departureAirports, selectedDestinations = [], accomIsLocked, onUpdate, error }) => {
    const [flatDestinations, setFlatDestinations] = useState(null);
    const [filteredDestinations, setFilteredDestinations] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [filterText, setFilterText] = useState("");
    const dataSource = useContext(DataSourceContext);
    const isMobile = useContext(IsMobileContext);

    const isVisuallySelected = (code) => isVisuallySelectedFor(selectedDestinations, code);

    useEffect(() => {
        async function fetchData() {
            // Don't blank the list while re-fetching (e.g. when switching search
            // type) - keeping the previous data avoids the field flashing
            // "Loading..." on every tab switch. It's null only on first load.
            if (departureAirports.length > 0) {
                var destinations = await dataSource.getDestinations(departureAirports);
                setFlatDestinations(destinations);
                // Prune any selected destinations that aren't available for the
                // current airports / search type. Hotels (numeric codes) only
                // come from text search and aren't in the browse tree, so keep
                // those.
                if (selectedDestinations.length > 0 && !accomIsLocked) {
                    const valid = new Set();
                    for (const country of destinations.countries) {
                        valid.add(country.code);
                        for (const region of (country.regions || [])) {
                            valid.add(region.code);
                            for (const resort of (region.resorts || [])) {
                                valid.add(resort.code);
                            }
                        }
                    }
                    const kept = selectedDestinations.filter((d) => valid.has(d.code) || isHotelCode(d.code));
                    if (kept.length != selectedDestinations.length) {
                        onUpdate(kept);
                    }
                }
            }
        }
        fetchData();
    }, [departureAirports]);

    useEffect(() => {
        if (!filterText) {
            setFilteredDestinations(null);
            return;
        }
        // Debounce: wait for a pause in typing before hitting the search API, so
        // "greece" is one request rather than six. `cancelled` also drops a
        // stale in-flight response if the query changed again before it landed.
        let cancelled = false;
        setFilteredDestinations(null);
        const handle = setTimeout(async () => {
            const destinations = await dataSource.getDestinationsWithTextSearch(departureAirports, filterText);
            if (!cancelled) setFilteredDestinations(destinations);
        }, 250);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [filterText]);

    // Clear the search text on close so the field shows the selection summary
    // (placeholder), not leftover typed text.
    useEffect(() => {
        if (!isOpen) {
            setFilterText("");
        }
    }, [isOpen]);

    // Toggle a destination in/out of the selection (tree cascade / hotel rules
    // live in helpers/destinationSelection.js). The popover stays open so
    // several can be picked in one go.
    const onSelect = (selected) => onUpdate(toggleDestination(flatDestinations, selectedDestinations, selected));

    const removeDestination = (code) => {
        onUpdate(selectedDestinations.filter((d) => d.code != code));
    };

    let inputText = "Destination or Hotel";
    if (selectedDestinations.length == 1) {
        inputText = selectedDestinations[0].name;
    } else if (selectedDestinations.length > 1) {
        inputText = `${selectedDestinations.length} destinations`;
    }

    let entries = [];
    if (filteredDestinations) {
        // Render in the order the server returns (geography first, then hotels &
        // tours) and drop in a separator at the geography -> hotels boundary.
        const allResults = filteredDestinations.destinations || [];
        let geoSeen = false;
        let separatorAdded = false;
        allResults.forEach((d, i) => {
            const isHotel = d.type == "Hotel";
            if (!isHotel) geoSeen = true;
            if (isHotel && geoSeen && !separatorAdded) {
                entries.push(
                    <div key="hotels-separator" className="flex items-center gap-2 px-2 pt-3 pb-1">
                        <span className="flex-1 border-t border-content_4"></span>
                        <span className="text-xs font-semibold uppercase tracking-wide text-content_3">Hotels &amp; Tours</span>
                        <span className="flex-1 border-t border-content_4"></span>
                    </div>
                );
                separatorAdded = true;
            }
            entries.push(<DetailedSelectItem destination={d} key={i} onSelect={onSelect} selected={isVisuallySelected(d.code)} />);
        });
    } else {
        if (flatDestinations) {
            for (let index = 0; index < flatDestinations.countries.length; index++) {
                const country = flatDestinations.countries[index];
                entries.push(<SimpleSelectItem destination={country} key={index} onSelect={onSelect} type="country" selected={isVisuallySelected(country.code)} />);
                for (let rIndex = 0; rIndex < country.regions.length; rIndex++) {
                    const element = country.regions[rIndex];
                    // A catch-all "<country> all resorts" region isn't a real
                    // location - skip its row and surface the resorts directly
                    // under the country instead.
                    const isAllResorts = element.name && element.name.toLowerCase().trim().endsWith("all resorts");
                    if (!isAllResorts) {
                        entries.push(<SimpleSelectItem destination={element} key={index + "_" + rIndex} onSelect={onSelect} type="region" selected={isVisuallySelected(element.code)} />)
                    }
                    // Resorts ("locations") nested under each region; promoted to
                    // the region indent when the wrapper region is skipped.
                    const resorts = element.resorts || [];
                    for (let sIndex = 0; sIndex < resorts.length; sIndex++) {
                        const resort = resorts[sIndex];
                        entries.push(<SimpleSelectItem destination={resort} key={index + "_" + rIndex + "_" + sIndex} onSelect={onSelect} type={isAllResorts ? "region" : "resort"} selected={isVisuallySelected(resort.code)} />)
                    }
                }
            }
        }
    }

    // Header carries the mobile search box and the selected-destination chips.
    // The chips strip is always rendered at a fixed single-row height (chips
    // scroll horizontally) so picking the first destination doesn't push the
    // list down - it just fills the already-reserved space.
    let header = (
        <div className="space-y-2">
            {isMobile && (
                <div className="relative">
                    <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)} className="form-input form-input--with-icon" placeholder={"Search"} />
                    <span className="absolute left-3 top-[20px] -translate-y-1/2 pointer-events-none text-content_2">
                        {icon}
                    </span>
                </div>
            )}
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide min-h-[40px]">
                {selectedDestinations.length === 0 ? (
                    <span className="text-sm text-content_3 px-1">Pick one or more destinations</span>
                ) : (
                    selectedDestinations.map((d) => (
                        <span key={d.code} className="shrink-0 inline-flex items-center gap-1 bg-ocean-100 text-primary text-sm rounded-full pl-3 pr-1 py-1">
                            {d.name}
                            <button type="button" onClick={() => removeDestination(d.code)} aria-label={"Remove " + d.name}
                                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-ocean-200 text-content_2 hover:text-primary">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                            </button>
                        </span>
                    ))
                )}
            </div>
        </div>
    );

    // A "Done" button to close once selections are made (clicks toggle rather
    // than close, so we need an explicit way out).
    const footer = <PickerFooter onClear={() => onUpdate([])} onConfirm={() => setIsOpen(false)} clearDisabled={selectedDestinations.length == 0} />;

    // Empty-state: distinguish "still loading a search" from genuinely no
    // results / no destinations available (e.g. an index is unavailable).
    let emptyState = null;
    if (entries.length == 0) {
        if (filterText && filteredDestinations == null) {
            emptyState = null; // search in flight
        } else if (filterText) {
            emptyState = <div className="text-content_2 text-sm p-3 text-center">No matches for &ldquo;{filterText}&rdquo;.</div>;
        } else {
            emptyState = <div className="text-content_2 text-sm p-3 text-center">No destinations available.</div>;
        }
    }

    return <PopoverInput error={error} header={header} footer={footer} panelWidthClass={isMobile ? "" : "w-[400px]"} disabled={departureAirports.length == 0 || accomIsLocked} icon={icon} inputText={inputText} isLoading={flatDestinations == null} onTextUpdate={setFilterText} filterText={filterText} isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="">
            {entries.length > 0 ? entries : emptyState}
        </div>
    </PopoverInput>
}
export default DestinationPicker;
